import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FaLeaf, FaUser } from "react-icons/fa";
import { FiClock } from "react-icons/fi";
import { Button } from "../../../ui/Button";
import { Input } from "../../../ui/Input";
import { PasswordField } from "../../../ui/PasswordField";
import { ImageUpload } from "../../../ui/ImageUpload";
import * as S from "../Login.styled";
import PasswordStrengthMeter from "../PasswordStrengthMeter";

/**
 * Farmer registration, one decision at a time.
 *
 * A farm needs its login, the person behind it, and the farm itself — nine
 * fields, which as a single screen read as a wall and buried the one field that
 * actually decides whether the application can be reviewed (the farm name) at
 * the very bottom. Each step here validates only its own fields, so a farmer is
 * never told about a mistake three screens away, and the last step shows the
 * whole application back before it is sent for approval.
 *
 * Shoppers keep the single short form — five fields do not need a wizard.
 */
import SocialAuthButtons, { VerifiedSocialAccount } from "../SocialAuth";

const STEPS = [
  {
    key: "account",
    fields: ["email", "password", "confirmPassword"],
    titleKey: "login.wizard.step_account",
    titleDefault: "Your login",
    hintKey: "login.wizard.step_account_hint",
    hintDefault: "You'll use this to sign in and manage your farm.",
    labelKey: "login.wizard.tab_account",
    labelDefault: "Login",
  },
  {
    key: "person",
    fields: ["firstName", "lastName"],
    titleKey: "login.wizard.step_person",
    titleDefault: "Who runs the farm",
    hintKey: "login.wizard.step_person_hint",
    hintDefault: "Shoppers see your name on your farm page. A photo is optional.",
    labelKey: "login.wizard.tab_person",
    labelDefault: "You",
  },
  {
    key: "farm",
    fields: ["businessName"],
    titleKey: "login.wizard.step_farm",
    titleDefault: "Your farm",
    hintKey: "login.wizard.step_farm_hint",
    hintDefault:
      "The name shoppers will search for. You can add produce and baskets once your farm is approved.",
    labelKey: "login.wizard.tab_farm",
    labelDefault: "Farm",
  },
  {
    key: "review",
    fields: [],
    titleKey: "login.wizard.step_review",
    titleDefault: "Check and send",
    hintKey: "login.wizard.step_review_hint",
    hintDefault: "Make sure this is right — we review each farm by hand.",
    labelKey: "login.wizard.tab_review",
    labelDefault: "Send",
  },
];

const VillagerSignupWizard = ({
  formData,
  socialProfile = null,
  errors,
  touched,
  submitting,
  notification,
  onChange,
  onBlur,
  onFieldUpload,
  onRoleChange,
  onValidateStep,
  onSubmit,
  onLoginClick,
}) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(0);

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  /** Only this step's fields gate it — a later step's blanks are not yet errors. */
  const goNext = () => {
    const firstInvalid = onValidateStep(step.fields);
    if (firstInvalid) {
      document.getElementById(firstInvalid)?.focus();
      return;
    }
    setStepIndex((i) => Math.min(STEPS.length - 1, i + 1));
  };

  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));

  // Enter advances rather than submitting from step one, which would post an
  // application with no farm name on it.
  const handleFormSubmit = (event) => {
    if (!isLast) {
      event.preventDefault();
      goNext();
      return;
    }
    onSubmit(event);
  };

  const notEntered = t("login.wizard.review_empty", { defaultValue: "Not given" });

  return (
    <S.FormWrapper data-testid="villager-wizard">
      <S.FormHeader>
        <S.HeaderIcon aria-hidden="true">
          <FaLeaf />
        </S.HeaderIcon>
        <h1>{t("login.wizard.title", { defaultValue: "Set up your farm" })}</h1>
        <S.Subtitle>
          {t("login.wizard.subtitle", {
            defaultValue: "Four short steps. Nothing is sent until the last one.",
          })}
        </S.Subtitle>
      </S.FormHeader>

      <S.Stepper aria-label={t("login.wizard.progress_label", { defaultValue: "Progress" })}>
        {STEPS.map((entry, index) => {
          const state =
            index < stepIndex ? "done" : index === stepIndex ? "current" : "todo";
          return (
            <S.StepMarker
              key={entry.key}
              $state={state}
              aria-current={state === "current" ? "step" : undefined}
            >
              {t(entry.labelKey, { defaultValue: entry.labelDefault })}
            </S.StepMarker>
          );
        })}
      </S.Stepper>

      {step.key === "account" && (socialProfile
        ? <VerifiedSocialAccount profile={socialProfile} />
        : <SocialAuthButtons role="Villager" />)}
      <S.Form onSubmit={handleFormSubmit} noValidate>
        <div>
          <S.StepCount>
            {t("login.wizard.progress", {
              current: stepIndex + 1,
              total: STEPS.length,
              defaultValue: "Step {{current}} of {{total}}",
            })}
          </S.StepCount>
          <S.StepTitle>{t(step.titleKey, { defaultValue: step.titleDefault })}</S.StepTitle>
          <S.StepHint>{t(step.hintKey, { defaultValue: step.hintDefault })}</S.StepHint>
        </div>

        <S.StepFields>
          {step.key === "account" ? (
            <>
              <Input
                id="email"
                name="email"
                readOnly={Boolean(socialProfile)}
                label={t("login.email", { defaultValue: "Email" })}
                placeholder={t("login.email_placeholder", {
                  defaultValue: "example@example.com",
                })}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={formData.email}
                onChange={onChange}
                onBlur={onBlur}
                errorText={touched.email ? errors.email : undefined}
                required
              />
              {!socialProfile && <>
              <S.FieldGroup>
                <PasswordField
                  id="password"
                  name="password"
                  label={t("login.password", { defaultValue: "Password" })}
                  placeholder={t("login.password_placeholder", {
                    defaultValue: "Password",
                  })}
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorText={touched.password ? errors.password : undefined}
                  required
                />
                <PasswordStrengthMeter password={formData.password} />
              </S.FieldGroup>
              <PasswordField
                id="confirmPassword"
                name="confirmPassword"
                label={t("login.confirm_password", { defaultValue: "Confirm Password" })}
                placeholder={t("login.confirm_password_placeholder", {
                  defaultValue: "Confirm Password",
                })}
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={onChange}
                onBlur={onBlur}
                errorText={
                  touched.confirmPassword ? errors.confirmPassword : undefined
                }
                required
              />
              </>}
              {/* Kept on step one only: a mis-answered "are you a farm?" is
                  cheap to fix here and expensive to discover at the end. */}
              <S.FieldGroup>
                <S.FieldLabel>
                  {t("login.account_type", { defaultValue: "Account type" })}
                </S.FieldLabel>
                <S.RoleToggle>
                  <S.RoleOption
                    type="button"
                    $active={false}
                    onClick={() => onRoleChange("Client")}
                    aria-pressed={false}
                  >
                    <S.RoleIcon aria-hidden="true">
                      <FaUser />
                    </S.RoleIcon>
                    {t("login.account_client", { defaultValue: "Client" })}
                  </S.RoleOption>
                  <S.RoleOption
                    type="button"
                    $active
                    onClick={() => onRoleChange("Villager")}
                    aria-pressed
                  >
                    <S.RoleIcon aria-hidden="true">
                      <FaLeaf />
                    </S.RoleIcon>
                    {t("login.account_villager", { defaultValue: "Villager" })}
                  </S.RoleOption>
                </S.RoleToggle>
              </S.FieldGroup>
            </>
          ) : null}

          {step.key === "person" ? (
            <>
              <S.FieldRow>
                <Input
                  id="firstName"
                  name="firstName"
                  label={t("login.first_name", { defaultValue: "First Name" })}
                  placeholder={t("login.first_name_placeholder", {
                    defaultValue: "First Name",
                  })}
                  type="text"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorText={touched.firstName ? errors.firstName : undefined}
                  required
                />
                <Input
                  id="lastName"
                  name="lastName"
                  label={t("login.last_name", { defaultValue: "Last Name" })}
                  placeholder={t("login.last_name_placeholder", {
                    defaultValue: "Last Name",
                  })}
                  type="text"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={onChange}
                  onBlur={onBlur}
                  errorText={touched.lastName ? errors.lastName : undefined}
                  required
                />
              </S.FieldRow>
              {/* Uploading needs a signed-in token and nobody is signed in yet, so the
                  photo is set from the profile once the account exists. */}
              <S.StepHint>
                {t("login.photo_after_signup", {
                  defaultValue: "You can add a profile photo from your profile once you're in.",
                })}
              </S.StepHint>
            </>
          ) : null}

          {step.key === "farm" ? (
            <>
              <Input
                id="businessName"
                name="businessName"
                label={t("login.business_name", { defaultValue: "Business Name" })}
                placeholder={t("login.business_name_placeholder", {
                  defaultValue: "Farm Co.",
                })}
                type="text"
                autoComplete="organization"
                value={formData.businessName}
                onChange={onChange}
                onBlur={onBlur}
                errorText={touched.businessName ? errors.businessName : undefined}
                required
              />
              <Input
                id="businessDescription"
                name="businessDescription"
                label={t("login.business_description", {
                  defaultValue: "Business Description",
                })}
                placeholder={t("login.business_description_placeholder", {
                  defaultValue: "Describe your business",
                })}
                type="text"
                value={formData.businessDescription}
                onChange={onChange}
              />
              <ImageUpload
                label={t("login.business_banner", { defaultValue: "Business Banner" })}
                currentUrl={formData.businessBannerPicUrl}
                folder="farms"
                onUpload={(url) => onFieldUpload("businessBannerPicUrl", url)}
              />
            </>
          ) : null}

          {step.key === "review" ? (
            <>
              <S.ReviewList>
                <div>
                  <dt>{t("login.email", { defaultValue: "Email" })}</dt>
                  <dd>{formData.email}</dd>
                </div>
                <div>
                  <dt>{t("login.wizard.review_person", { defaultValue: "Run by" })}</dt>
                  <dd>{`${formData.firstName} ${formData.lastName}`.trim()}</dd>
                </div>
                <div>
                  <dt>{t("login.business_name", { defaultValue: "Business Name" })}</dt>
                  <dd>{formData.businessName}</dd>
                </div>
                <div>
                  <dt>
                    {t("login.business_description", {
                      defaultValue: "Business Description",
                    })}
                  </dt>
                  <dd>{formData.businessDescription.trim() || notEntered}</dd>
                </div>
              </S.ReviewList>
              <S.ReviewNote>
                <FiClock aria-hidden="true" size={18} />
                {t("login.wizard.review_note", {
                  defaultValue:
                    "We review each farm by hand, usually within a day. We'll email you as soon as it's approved — then you can sign in and add your produce and baskets.",
                })}
              </S.ReviewNote>
            </>
          ) : null}
        </S.StepFields>

        {notification.message ? (
          <S.Notification $type={notification.type} role="status">
            {notification.message}
          </S.Notification>
        ) : null}

        <S.WizardNav>
          {stepIndex > 0 ? (
            <Button type="button" variant="outline" onClick={goBack}>
              {t("login.wizard.back", { defaultValue: "Back" })}
            </Button>
          ) : null}
          {isLast ? (
            <Button type="submit" variant="primary" loading={submitting}>
              {t("login.wizard.submit", { defaultValue: "Send my application" })}
            </Button>
          ) : (
            <Button type="submit" variant="primary">
              {t("login.wizard.next", { defaultValue: "Continue" })}
            </Button>
          )}
        </S.WizardNav>
      </S.Form>

      <S.ToggleText>
        {t("login.toggle_existing", { defaultValue: "Already have an account?" })}{" "}
        <S.ToggleAction type="button" onClick={onLoginClick}>
          {t("login.toggle_login", { defaultValue: "Log In" })}
        </S.ToggleAction>
      </S.ToggleText>
    </S.FormWrapper>
  );
};

export default VillagerSignupWizard;
